import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiCode, HiArrowLeft } from 'react-icons/hi';
import axios from 'axios';

// Components
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import About from './components/About';
import Projects from './components/Projects';
import Research from './components/Research';
import Skills from './components/Skills';
import Contact from './components/Contact';
import Footer from './components/Footer';
import BackgroundEffects from './components/BackgroundEffects';
import LoadingScreen from './components/LoadingScreen';
import AppsDetail from './components/AppsDetail';
import ResearchDetail from './components/ResearchDetail';
import XboxExploit from './components/XboxExploit';
import Genie3AIGame from './components/Genie3AIGame';
import BirthdayPage from './components/BirthdayPage';
import BirthdayToast from './components/BirthdayToast';
import BirthdayCardViewer from './components/BirthdayCardViewer';
import BirthdayCardCreatorPage from './components/BirthdayCardCreatorPage';
import LenisProvider from './components/LenisProvider';
import MusicPlayer from './components/MusicPlayer';
import SingleTrack from './components/SingleTrack';
import AdminPanel from './components/AdminPanel';
import CryptalRecordsArticle from './components/CryptalRecordsArticle';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      // Wait for the DOM to render the new route content
      setTimeout(() => {
        const target = document.querySelector(hash);
        if (target) {
          // Use Lenis smooth scroll if available
          if (window.lenis) {
            window.lenis.scrollTo(target, { offset: -80 });
          } else {
            target.scrollIntoView({ behavior: 'smooth' });
          }
          return;
        }
        // Scroll to top with Lenis if available
        if (window.lenis) {
          window.lenis.scrollTo(0, { immediate: true });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
      }, 100);
    } else {
      // Scroll to top with Lenis if available
      if (window.lenis) {
        window.lenis.scrollTo(0, { immediate: true });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    }
  }, [pathname, hash]);
  return null;
}

// Conditional Background Effects Component
const ConditionalBackgroundEffects = () => {
  const location = useLocation();
  const isResearchPage = location.pathname.includes('/research/') || location.pathname.includes('/xbox-360/');
  const isSingleTrackPage = location.pathname.includes('/music/') && location.pathname !== '/music';
  const isBirthdayCardPage = location.pathname.includes('/birthday-card/');
  
  // Only show BackgroundEffects on main site pages, not research pages, single track pages, or birthday card pages
  if (isResearchPage || isSingleTrackPage || isBirthdayCardPage) {
    return null;
  }
  
  return <BackgroundEffects />;
};

// AppContent component to use useLocation hook within Router context
const AppContent = ({ loading, setLoading, portfolioData, setPortfolioData, isMusicRoute }) => {
  const location = useLocation();
  
  // Check if current route is music-related or birthday card related (skip loading screen for these routes)
  const isMusicRouteCurrent = location.pathname.startsWith('/music');
  const isBirthdayCardRoute = location.pathname.startsWith('/birthday-card');

  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        const [projectsRes, researchRes, skillsRes, experienceRes, statsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/projects`),
          axios.get(`${API_BASE_URL}/api/research`),
          axios.get(`${API_BASE_URL}/api/skills`),
          axios.get(`${API_BASE_URL}/api/experience`),
          axios.get(`${API_BASE_URL}/api/portfolio-stats`)
        ]);

        setPortfolioData({
          projects: projectsRes.data.projects || [],
          research: researchRes.data.research || [],
          skills: skillsRes.data.skills || [],
          experience: experienceRes.data.experience || [],
          stats: statsRes.data || {}
        });
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
        // Set default data if API fails
        setPortfolioData({
          projects: [],
          research: [],
          skills: [],
          experience: [],
          stats: {}
        });
      } finally {
        // Minimum loading time for smooth experience, but skip for music and birthday card routes
        if (isMusicRouteCurrent || isBirthdayCardRoute) {
          setLoading(false);
        } else {
          setTimeout(() => setLoading(false), 3000);
        }
      }
    };

    fetchPortfolioData();
  }, [isMusicRouteCurrent, isBirthdayCardRoute, setLoading, setPortfolioData]);

  if (loading && !isMusicRouteCurrent && !isBirthdayCardRoute) {
    return <LoadingScreen />;
  }

  return (
    <>
      <ScrollToTop />
      <div className="App relative min-h-screen bg-primary text-white overflow-x-hidden px-4 sm:px-6 md:px-8">
        <ConditionalBackgroundEffects />
        
        <AnimatePresence mode="wait">
          <Routes>
          <Route path="/" element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <Navigation />
              <main>
                <Hero />
                <About />
                <Projects projects={portfolioData.projects} />
                <Research research={portfolioData.research} />
                <Skills skills={portfolioData.skills} />
                <section id="apps" className="py-32 scroll-mt-20">
                  <div className="max-w-7xl mx-auto px-6 md:text-center text-left">
                    <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-6">
                      <span className="font-jetbrains text-accent-green tracking-wider text-sm">APPLICATIONS</span>
                    </div>
                    <h2 className="text-display-2 font-space-grotesk font-bold mb-8 md:text-center text-left">
                      <span className="text-white">Educational </span>
                      <span className="gradient-text-green">Software</span>
                    </h2>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 md:text-center text-left">
                      Download essential development tools and educational software for your programming journey.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-start">
                      <motion.div
                        whileHover={{ scale: 1.05, y: -5 }}
                        className="glass-morphism-strong rounded-2xl p-8 interactive-card md:text-center text-left"
                      >
                        <div className="flex items-center mb-6">
                          <div className="w-12 h-12 bg-accent-green/20 rounded-lg flex items-center justify-center mr-4">
                            <img 
                              src="/apps/delphi-2010/delphi-2010.png" 
                              alt="Delphi 2010" 
                              className="w-6 h-6 object-contain"
                            />
                          </div>
                          <h3 className="text-2xl font-space-grotesk font-bold text-white md:text-center text-left">Delphi 2010</h3>
                        </div>
                        <p className="text-gray-300 mb-6 md:text-center text-left">
                          The default IDE used in South African schools according to the CAPS curriculum. 
                          Lightweight version for educational purposes.
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-accent-green font-jetbrains">~105MB</span>
                          <Link to="/apps/delphi-2010" className="btn-primary">
                            Learn More →
                          </Link>
                        </div>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.05, y: -5 }}
                        className="glass-morphism-strong rounded-2xl p-8 interactive-card md:text-center text-left"
                      >
                        <div className="flex items-center mb-6">
                          <div className="w-12 h-12 bg-accent-blue/20 rounded-lg flex items-center justify-center mr-4">
                            <img 
                              src="/apps/delphi-12/delphi-12.png" 
                              alt="Delphi 12" 
                              className="w-6 h-6 object-contain"
                            />
                          </div>
                          <h3 className="text-2xl font-space-grotesk font-bold text-white md:text-center text-left">Delphi 12</h3>
                        </div>
                        <p className="text-gray-300 mb-6 md:text-center text-left">
                          The latest version of Delphi for modern development with enhanced features 
                          and improved performance.
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-accent-blue font-jetbrains">~1.5GB</span>
                          <Link to="/apps/delphi-12" className="btn-primary">
                            Learn More →
                          </Link>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </section>
                <Contact />
              </main>
              <Footer />
              <BirthdayToast />
            </motion.div>
          } />
          
          <Route path="/project/:id" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <ProjectDetail />
            </motion.div>
          } />
          
          <Route path="/research/:id" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <ResearchDetail />
            </motion.div>
          } />
          
          <Route path="/apps/:appId" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <AppsDetail />
            </motion.div>
          } />
          
          <Route path="/xbox-360/exploits/bad-update" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <XboxExploit />
            </motion.div>
          } />
          
          <Route path="/research/genie-3-ai-game" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <Genie3AIGame />
            </motion.div>
          } />
          
          <Route path="/cryptal-records_breakingthemusicindustry" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <CryptalRecordsArticle />
            </motion.div>
          } />
          
          <Route path="/birthdays" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <BirthdayPage />
            </motion.div>
          } />
          
          <Route path="/birthday-card/:cardId" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <BirthdayCardViewer />
            </motion.div>
          } />
          
          <Route path="/create-birthday-card" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <BirthdayCardCreatorPage />
            </motion.div>
          } />
          
          <Route path="/music" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <MusicPlayer />
            </motion.div>
          } />
          
          <Route path="/music/:trackId" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <SingleTrack />
            </motion.div>
          } />
          
          <Route path="/admin" element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <AdminPanel />
            </motion.div>
          } />
        </Routes>
      </AnimatePresence>
      </div>
    </>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState({
    projects: [],
    research: [],
    skills: [],
    experience: [],
    stats: {}
  });

  return (
    <LenisProvider>
      <Router>
        <AppContent 
          loading={loading} 
          setLoading={setLoading} 
          portfolioData={portfolioData} 
          setPortfolioData={setPortfolioData} 
        />
      </Router>
    </LenisProvider>
  );
}

// Placeholder component for detailed views
const ProjectDetail = () => (
  <div className="min-h-screen px-6">
    <div className="max-w-4xl mx-auto pt-20">
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors">
          <HiArrowLeft className="mr-2" />
          Back to Home
        </Link>
      </div>
      <div className="glass-morphism-strong rounded-2xl p-8">
        <h1 className="text-display-3 font-space-grotesk font-bold gradient-text-green mb-6">
          Project Detail
        </h1>
        <p className="text-gray-300">Project detail page coming soon...</p>
      </div>
    </div>
  </div>
);

export default App;